import { useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageCode, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, safeLanguageCode } from '../utils/languageUtils';

// Guard component for catch-all route that checks if language prefix exists
function CatchAllRedirect() {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0];
  
  // Check if URL already has a valid language prefix
  const hasLanguagePrefix = SUPPORTED_LANGUAGES.includes(safeLanguageCode(firstSegment));
  
  // If already has language prefix or is root path, don't redirect
  if (hasLanguagePrefix || location.pathname === '/') {
    return null;
  }
  
  // Redirect to default language with current path
  return (
    <Navigate 
      to={`/${DEFAULT_LANGUAGE}${location.pathname}${location.search}${location.hash}`} 
      replace 
    />
  );
}
import Home from '../pages/Home';
import Register from '../pages/Register';
import RegistrationSuccess from '../pages/RegistrationSuccess';
import Mentions from '../pages/Mentions';
import AuthVerify from '../pages/AuthVerify';
import AccountPreferences from '../pages/AccountPreferences';
import MonitoredIdentities from '../pages/MonitoredIdentities';
import EmailConfirmed from '../pages/EmailConfirmed';
import EmailConfirmation from '../pages/EmailConfirmation';
import Unsubscribe from '../pages/Unsubscribe';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import LegalDisclaimer from '../pages/LegalDisclaimer';
import FAQ from '../pages/FAQ';
import WhyMonitoringMatters from '../pages/WhyMonitoringMatters';
import OurCommitment from '../pages/OurCommitment';
import HowItWorks from '../pages/HowItWorks';
import PrivateRoute from './PrivateRoute';
import AdminRoute from './AdminRoute';
import AdminBackoffice from './admin/AdminBackoffice';
import AdminDashboard from '../pages/admin/AdminDashboard';
import UserManagement from '../pages/admin/UserManagement';
import UserDetailPage from '../pages/admin/UserDetailPage';
import EmailQueuePage from '../pages/admin/EmailQueuePage';
import PublicationMonitoring from '../pages/admin/PublicationMonitoring';
import PublicationDetailPage from '../pages/admin/PublicationDetailPage';
import ComingSoon from './admin/ComingSoon';

// Component to handle language-specific routes
function LanguageRoutesWrapper() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const langCode = lang ? safeLanguageCode(lang) : DEFAULT_LANGUAGE;
    
    // Validate language parameter
    if (!lang || !SUPPORTED_LANGUAGES.includes(lang as LanguageCode)) {
      // Redirect to default language route
      // If lang exists but is invalid, remove it with trailing slash
      // If lang doesn't exist, path should already be correct (no language prefix)
      const pathWithoutLang = lang 
        ? location.pathname.replace(/^\/[a-z]{2}\//, '/')
        : location.pathname;
      navigate(`/${DEFAULT_LANGUAGE}${pathWithoutLang}${location.search}${location.hash}`, { replace: true });
      return;
    }
    
    // Change language if needed
    if (i18n.language !== langCode) {
      i18n.changeLanguage(langCode);
    }
  }, [lang, i18n, location, navigate]);

  // Memoize the routes structure to prevent unnecessary re-renders
  // Only recalculate when the language changes
  const memoizedRoutes = useMemo(() => {
    // Don't render routes if language is invalid
    if (!lang || !SUPPORTED_LANGUAGES.includes(safeLanguageCode(lang))) {
      return null;
    }

    return (
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Home />} />
        <Route path="/registrarse" element={<Register />} />
        <Route path="/register" element={<Register />} />
        <Route path="/registro-exitoso" element={<RegistrationSuccess />} />
        <Route path="/registration-success" element={<RegistrationSuccess />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        <Route path="/confirmar-email" element={<EmailConfirmation />} />
        <Route path="/email-confirmation" element={<EmailConfirmation />} />
        <Route path="/email-confirmado" element={<EmailConfirmed />} />
        <Route path="/email-confirmed" element={<EmailConfirmed />} />
        <Route path="/confirmar-email/:token" element={<EmailConfirmation />} />
        <Route path="/confirm-email/:token" element={<EmailConfirmation />} />
        <Route path="/darse-de-baja/:token" element={<Unsubscribe />} />
        <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
        <Route path="/politica-de-privacidad" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/aviso-legal" element={<LegalDisclaimer />} />
        <Route path="/legal-disclaimer" element={<LegalDisclaimer />} />
        <Route path="/preguntas-frecuentes" element={<FAQ />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/por-que-monitorear-importa" element={<WhyMonitoringMatters />} />
        <Route path="/why-monitoring-matters" element={<WhyMonitoringMatters />} />
        <Route path="/nuestro-compromiso" element={<OurCommitment />} />
        <Route path="/our-commitment" element={<OurCommitment />} />
        <Route path="/como-funciona" element={<HowItWorks />} />
        <Route path="/how-it-works" element={<HowItWorks />} />

        {/* Protected routes - require authentication */}
        <Route path="/menciones" element={
          <PrivateRoute>
            <Mentions />
          </PrivateRoute>
        } />
        <Route path="/mentions" element={
          <PrivateRoute>
            <Mentions />
          </PrivateRoute>
        } />
        <Route path="/preferencias-de-cuenta" element={
          <PrivateRoute>
            <AccountPreferences />
          </PrivateRoute>
        } />
        <Route path="/account-preferences" element={
          <PrivateRoute>
            <AccountPreferences />
          </PrivateRoute>
        } />
        <Route path="/identidades-monitoreadas" element={
          <PrivateRoute>
            <MonitoredIdentities />
          </PrivateRoute>
        } />
        <Route path="/identidades-monitoreadas/nueva" element={
          <PrivateRoute>
            <MonitoredIdentities />
          </PrivateRoute>
        } />
        <Route path="/monitored-identities" element={
          <PrivateRoute>
            <MonitoredIdentities />
          </PrivateRoute>
        } />
        <Route path="/monitored-identities/new" element={
          <PrivateRoute>
            <MonitoredIdentities />
          </PrivateRoute>
        } />

        {/* Admin routes - require admin privileges */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminBackoffice />
          </AdminRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="user-management" element={<UserManagement />} />
          <Route path="gestion-de-usuarios" element={<UserManagement />} />
          <Route path="users/:userId" element={<UserDetailPage />} />
          <Route path="email-queue" element={<EmailQueuePage />} />
          <Route path="cola-de-correos" element={<EmailQueuePage />} />
          <Route path="publications" element={<PublicationMonitoring />} />
          <Route path="publications/:publicationId" element={<PublicationDetailPage />} />
          <Route path="publicaciones" element={<PublicationMonitoring />} />
          <Route path="publicaciones/:publicationId" element={<PublicationDetailPage />} />
          <Route path="settings" element={<ComingSoon featureName="Settings" />} />
          <Route path="configuracion" element={<ComingSoon featureName="Configuración" />} />
          <Route path="reports" element={<ComingSoon featureName="Reports" />} />
          <Route path="reportes" element={<ComingSoon featureName="Reportes" />} />
        </Route>
      </Routes>
    );
  }, [lang]); // Only recalculate when language parameter changes

  return memoizedRoutes;
}

// Main language router component
export default function LanguageRouter() {
  return (
    <Routes>
      {/* Language-prefixed routes */}
      <Route path="/:lang/*" element={<LanguageRoutesWrapper />} />
      
      {/* Root redirect to default language */}
      <Route path="/" element={<Navigate to={`/${DEFAULT_LANGUAGE}/`} replace />} />
      
      {/* Catch-all for routes without language prefix - always present, logic handled in component */}
      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  );
}